/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type { BuyBreakTier, ProductBuyBreaksInfo } from "@farmlands/commerce";

import { formatPrice } from "../../../util/helpers";
import { ProductDetailsPriceBreaks } from "../storage";

import { BUY_MORE_AND_SAVE_LABEL } from "./getPromotionBadges";

const formatQuantityRangeTitle = (qtyFrom: number, qtyTo: number, unit?: string): string => {
  const range = qtyTo > qtyFrom ? `${qtyFrom}–${qtyTo}` : `${qtyFrom}+`;
  return unit ? `Buy ${range} ${unit}` : `Buy ${range}`;
};

const resolveTierTo = (to: number): number => (to !== 0 ? to : Number.POSITIVE_INFINITY);

const resolveTierFrom = (from: number): number => (from === 0 ? 1 : from);

const toBuyBreakTier = (buyBreak: ProductDetailsPriceBreaks): BuyBreakTier => {
  const from = resolveTierFrom(buyBreak.from);
  const tier: BuyBreakTier = {
    tierTitle: formatQuantityRangeTitle(from, buyBreak.to, buyBreak.unit),
    tierPrice: formatPrice(buyBreak.netUnitPriceExcGst),
    from,
    to: resolveTierTo(buyBreak.to),
  };

  if (buyBreak.effectiveUnitDiscountPercentage > 0) {
    tier.promotionBadgeText = `Save ${buyBreak.effectiveUnitDiscountPercentage}%`;
  }

  return tier;
};

export const toBuyBreakTiers = (
  breaks: ProductDetailsPriceBreaks[] | undefined
): BuyBreakTier[] => {
  if (!breaks?.length) {
    return [];
  }

  return breaks.map(toBuyBreakTier).sort((a, b) => a.from - b.from);
};

/** Maps product-details price breaks into `ProductCard` / cart `buyBreaks` props. */
export const toProductBuyBreaksInfo = (
  breaks: ProductDetailsPriceBreaks[] | undefined
): ProductBuyBreaksInfo | undefined => {
  const tiers = toBuyBreakTiers(breaks);
  if (!tiers.length) {
    return undefined;
  }

  return {
    promotionTitle: BUY_MORE_AND_SAVE_LABEL,
    tiers,
  };
};
